package sarka.design.skoria.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.layout.onGloballyPositioned
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.ShadowRoot
import org.w3c.dom.mediacapture.MediaStream
import org.w3c.dom.mediacapture.MediaStreamTrack
import sarka.design.skoria.webrtc.PlatformVideoTrack

private fun composeShadowRoot(): ShadowRoot? =
    (document.body as? HTMLElement)
        ?.asDynamic()
        ?.shadowRoot
        ?.unsafeCast<ShadowRoot?>()

@Composable
actual fun VideoView(
    track: PlatformVideoTrack?,
    mirror: Boolean,
    muted: Boolean
) {
    val videoElement = remember {
        (document.createElement("video") as HTMLVideoElement).apply {
            autoplay = true
            controls = false
            style.position = "absolute"
            style.objectFit = "cover"
            style.zIndex = "9999"
            style.background = "black"
            style.transformOrigin = "center center"
        }
    }

    // Keep latest values for callbacks
    val mirrorState by rememberUpdatedState(mirror)
    val mutedState by rememberUpdatedState(muted)
    val trackState by rememberUpdatedState(track)

    // Parent inside shadow root (same logic as your original)
    var parentEl by remember { mutableStateOf<HTMLElement?>(null) }

    // Attach element into shadow root parent
    DisposableEffect(Unit) {
        val sr = composeShadowRoot()

        val wrapper = sr
            ?.querySelector("div")
            ?.unsafeCast<HTMLElement>()

        val parent = wrapper ?: (sr?.unsafeCast<HTMLElement?>())
        parentEl = parent

        if (parent != null) {
            // Ensure absolute children position relative to this parent
            if (parent.style.position.isNullOrBlank()) parent.style.position = "relative"
            parent.appendChild(videoElement)
        }

        onDispose {
            parentEl = null
            videoElement.pause()
            videoElement.srcObject = null
            videoElement.parentElement?.removeChild(videoElement)
        }
    }

    // Connect track to element
    DisposableEffect(trackState, mirrorState, mutedState) {
        videoElement.muted = mutedState
        videoElement.style.transform = if (mirrorState) "scaleX(-1)" else "scaleX(1)"

        val nativeTrack = trackState?.native as? MediaStreamTrack
        if (nativeTrack != null) {
            videoElement.srcObject = MediaStream().apply { addTrack(nativeTrack) }
            videoElement.play()
        } else {
            videoElement.srcObject = null
        }

        onDispose {
            // Keep element; just detach stream
            videoElement.pause()
            videoElement.srcObject = null
        }
    }

    // Compose anchor box: positions the DOM video
    Box(
        modifier = Modifier
            .fillMaxSize()
            .onGloballyPositioned { coordinates ->
            val parent = parentEl ?: return@onGloballyPositioned

            // Compose gives "window" coords; convert to CSS px, then to parent-local px
            val dpr = window.devicePixelRatio.takeIf { it > 0 } ?: 1.0

            val posDevice = coordinates.localToWindow(Offset.Zero) // often device px
            val posCssX = posDevice.x / dpr
            val posCssY = posDevice.y / dpr

            val sizeDevice = coordinates.size
            val widthCss = sizeDevice.width / dpr
            val heightCss = sizeDevice.height / dpr

            // Parent rect is in CSS px
            val rect = parent.getBoundingClientRect()
            val left = posCssX - rect.left
            val top = posCssY - rect.top

            videoElement.style.left = "${left}px"
            videoElement.style.top = "${top}px"
            videoElement.style.width = "${widthCss}px"
            videoElement.style.height = "${heightCss}px"
        }
    )
}
